import { createRoot } from "react-dom/client";
import { ThemeProvider } from "next-themes";
import { HelmetProvider } from "react-helmet-async";
import App from "./App.tsx";
import "./index.css";
import { startBuildVersionCheck } from "./lib/build-version-check";
import { logCacheEvent } from "./lib/cache-cleanup-log";

// Defensive cleanup: unregister any stale service workers and purge their
// caches so returning visitors don't get served broken chunk manifests after
// a redeploy. This project doesn't ship an app-shell service worker; any
// registration found here is leftover from a prior build or a misbehaving
// extension and should be evicted.
if (typeof window !== "undefined") {
  void (async () => {
    try {
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        const unregistered: string[] = [];
        const preserved: string[] = [];
        await Promise.allSettled(
          regs.map(async (r) => {
            const url =
              r.active?.scriptURL ??
              r.installing?.scriptURL ??
              r.waiting?.scriptURL ??
              "";
            // Leave messaging workers (e.g. firebase-messaging-sw.js) alone.
            if (/messaging|onesignal|fcm/i.test(url)) {
              preserved.push(url);
              return;
            }
            const ok = await r.unregister();
            if (ok) unregistered.push(url || r.scope);
          }),
        );
        if (regs.length > 0) {
          logCacheEvent("sw-cleanup", "info", "Service worker sweep", {
            unregistered,
            preserved,
          });
        }
      }
      if ("caches" in window) {
        const keys = await caches.keys();
        const targets = keys.filter((k) =>
          /workbox|precache|runtime|vite|assets|app-shell/i.test(k),
        );
        const skipped = keys.filter(
          (k) => !/workbox|precache|runtime|vite|assets|app-shell/i.test(k),
        );
        const results = await Promise.allSettled(
          targets.map((k) => caches.delete(k)),
        );
        const deleted = targets.filter(
          (_, i) => results[i].status === "fulfilled",
        );
        if (keys.length > 0) {
          logCacheEvent("sw-cleanup", "info", "Cache Storage sweep", {
            deleted,
            skipped,
          });
        }
      }
    } catch (err) {
      logCacheEvent("sw-cleanup", "error", "Cleanup failed", {
        message: err instanceof Error ? err.message : String(err),
      });
    }
  })();
}

// Poll the deployed index.html for a new build-id and hard-reload (with
// cache purge) when it changes — guarantees returning users never run
// against a stale chunk manifest after a deploy.
startBuildVersionCheck();

createRoot(document.getElementById("root")!).render(
  <HelmetProvider>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <App />
    </ThemeProvider>
  </HelmetProvider>
);
